import axios from 'axios';
import type { AxiosInstance, AxiosError } from 'axios';
/* CUU2-2822
import { FORCE_POLLING } from 'Constants/featureFlags';
import { ForcePollingFeatureFlags } from 'Providers/PollingProvider/index.types'; */
import { RootStore } from 'Stores/index';
import { AUTH0 } from './constants/env';
import { getLogger } from './utils/logger';
import { bugsnagClient } from './utils/logUtils';

const API: AxiosInstance = axios.create();

/** Throttle: only attempt token refresh on 401 at most once per this interval (ms). */
const UNAUTHORIZED_REFRESH_THROTTLE_MS = 60_000;
let last401RefreshAttemptAt = 0;

export const AUTH0_API: AxiosInstance = axios.create({
  baseURL: `https://${AUTH0?.domain}/api/v2/users/`,
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
  },
});

export const PURE_API: AxiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
  },
});

export const setAPIHeader = (key: string, value: string) => {
  API.defaults.headers.common[key] = value;
};
export const getAuthToken = () => {
  return API.defaults.headers.common.Authorization?.toString().split(' ')[1];
};
export const getBearerAuthToken = () => {
  return API.defaults.headers.common.Authorization;
};
export const removeAxiosAuthHeaders = () => {
  delete API.defaults.headers.common.Authorization;
};

/**
 * Defines the possible paths for messaging API endpoints.
 */
/* CUU2-2822
type MessagingPath = '/conversations' | '/messages' | '/link-preview'; */

/* CUU2-2822
interface SuppressedMessagingError {
  rootStore: RootStore;
  messagingPaths: MessagingPath[];
  err: AxiosError;
} */

/**
 * Checks if a messaging API error should be suppressed based on feature flags and returns true if suppressed.
 * Notifies Bugsnag if the error is suppressed.
 */
/* CUU2-2822
const handleSuppressedMessagingError = ({
  rootStore,
  messagingPaths,
  err,
}: SuppressedMessagingError): boolean => {

  // Check if the error should be suppressed based on feature flags
  const shouldSuppressErrorNotification = (
    rootStore.personStore.ldClient.variation(
      FORCE_POLLING,
      false
    ) as ForcePollingFeatureFlags
  )?.polling;

  // Check if the error is a messaging API error
  const isMessagingPath = messagingPaths.some((path) =>
    err.config?.url?.includes(path)
  );

  // Check if the error should be suppressed
  const shouldSuppressError =
    isMessagingPath && shouldSuppressErrorNotification;

  if (shouldSuppressError) {
    bugsnagClient.notify(err, (event) => {
      event.severity = 'info';
      event.context = 'Axios response interceptor';
      event.addMetadata('custom', {
        function: 'handleSuppressedMessagingError',
        message: 'Suppressed error for messaging API call due to polling.',
      });
    });
    return true; // Indicate that the error was handled (suppressed)
  }

  return false; // Indicate that the error was not handled here
}; */

/**
 * Configures `Axios` response interceptors:
 * - Redirect to / on 401 Unauthorized response.
 */
export const configureAxiosInterceptors = (rootStore: RootStore) => {
  // Redirect to / on 401 Unauthorized response.
  API.interceptors.response.use(null, async (err: AxiosError) => {
    /**
     * Handle suppressed messaging errors
     * Suppress errors that are part of the messaging API
     * and the polling feature flag is disabled
     */
    /* CUU2-2822
    if (
      handleSuppressedMessagingError({
        err,
        rootStore,
        messagingPaths: ['/conversations', '/messages', '/link-preview'],
      })
    ) {
      // Suppressed error, resolve the promise
      return Promise.resolve();
    } */

    //unauthenticated
    if (err.response && err.response.status === 401) {
      const logger = getLogger();
      logger.error(
        '[Axios Interceptor] 401 error caught by axios interceptor',
        {
          code: err.code,
          message: err.message,
          url: err.request?.responseURL,
          stack: err.stack,
        }
      );

      const now = Date.now();
      // Only attempt token refresh if it's been more than 1 minute since last attempt
      if (now - last401RefreshAttemptAt >= UNAUTHORIZED_REFRESH_THROTTLE_MS) {
        logger.info(`[Axios Interceptor] Attempting token refresh...`);

        last401RefreshAttemptAt = now;
        // Attempt to refresh the token. If there's an issue with the refresh, the user will be automatically logged out.
        const token = await rootStore.personStore.getRefreshedToken(true);

        // If the token was refreshed successfully, update the request headers
        // and login with the new token
        if (token) {
          logger.info(`[Axios Interceptor] Token refreshed successfully`);

          err.config.headers.Authorization = `Bearer ${token}`;
          await rootStore.personStore.loginWithExistingToken(token, false);

          logger.info(`[Axios Interceptor] Token updated internally`);
        } else {
          logger.info(`[Axios Interceptor] Token refresh failed`);
        }
      } else {
        logger.info(
          `[Axios Interceptor] Not attempting token refresh, last attempt was made ${
            (now - last401RefreshAttemptAt) / 1000
          } seconds ago (less than ${
            UNAUTHORIZED_REFRESH_THROTTLE_MS / 1000
          } seconds ago)`
        );
      }
    } else if (
      err?.response?.status === 409 &&
      err?.config?.url?.includes('/conversations/')
    ) {
      bugsnagClient.notify(err, (event) => {
        event.severity = 'info';
        event.context = 'Axios response interceptor';
        event.addMetadata('custom', {
          function: 'configureAxiosInterceptors',
          message: 'Name conflict when creating a new group.',
        });
      });
    } else if (err.response && err.response.status >= 400) {
      //client error
      bugsnagClient.notify(err, (event) => {
        event.severity = 'error';
        event.context = 'Axios response interceptor';
        event.addMetadata('custom', { function: 'configureAxiosInterceptors' });
      });
    } else if (err?.code === 'ECONNABORTED') {
      bugsnagClient.leaveBreadcrumb(
        `Request ${err.config?.method?.toUpperCase()} ${
          err.config?.url
        } timed-out.`
      );
    } else {
      //500 or other error
      console.error(
        'Axios error caught by interceptor, but err.response was undefined, cannot check for 401 response status'
      );
    }
    return Promise.reject(err);
  });
  API.interceptors.request.use(
    async (config) => {
      try {
        const token = await rootStore.personStore.getRefreshedToken();

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          await rootStore.personStore.loginWithExistingToken(token, false);
        }

        return config;
      } catch (error) {
        // Log the actual error for debugging
        console.error('Error in request interceptor:', error);
        return Promise.reject(error);
      }
    },
    (error) => {
      // Log more detailed error information
      console.error('Request interceptor error:', {
        error,
        config: error?.config,
        message: error?.message,
      });
      return Promise.reject(error);
    }
  );
};

export default API;
